<template>
  <div class="qr-scanner-wrapper">
    <camera-permission
      v-if="errorName"
      :error-name="errorName"
      @retry="retryCamera"
    />
    <div v-show="!errorName" id="qr-reader"></div>
  </div>
</template>

<script>
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode'
import CameraPermission from './CameraPermission.vue'

export default {
  name: 'QrScanner',
  components: { CameraPermission },
  props: {
    /**
     * ID de la cámara a usar. Si cambia en runtime, el scanner se reinicia.
     * El componente padre es responsable de listar las cámaras y permitir
     * al usuario elegir cuál usar (ver ScannerPackagesForDriverAssignmentView
     * en 12-horas-envios-app como referencia).
     */
    cameraId: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      scanner: null,
      result: null,
      errorName: null
    }
  },
  watch: {
    cameraId(newId) {
      if (newId && typeof newId === 'string') {
        // Mantener _selectedCamera sincronizado con la prop para que
        // startScanner() arranque con la cámara recién elegida y no
        // con la que se cacheó en loadCameras().
        this._selectedCamera = newId
        this.restartScanner()
      }
    }
  },
  async mounted() {
    await this.loadCameras()
  },
  beforeDestroy() {
    this.stopScanner()
  },
  methods: {
    async loadCameras() {
      try {
        const cameras = await Html5Qrcode.getCameras()

        if (!cameras.length) {
          this.errorName = 'NotFoundError'
          return
        }

        let selectedCamera = this.cameraId
        if (!selectedCamera) {
          // Preferir cámara trasera; fallback a la primera disponible.
          const backCamera = cameras.find(c =>
            /back|rear|environment/i.test(c.label || '')
          )
          selectedCamera = backCamera ? backCamera.id : cameras[0].id
        }

        this._selectedCamera = selectedCamera
        await this.startScanner()
      } catch (error) {
        this.errorName = error.name || 'UnknownError'
      }
    },
    async retryCamera() {
      this.errorName = null
      await this.loadCameras()
    },
    async restartScanner() {
      if (this._restarting) return
      this._restarting = true
      try {
        await this.stopScanner()
        await this.startScanner()
      } finally {
        this._restarting = false
      }
    },
    async startScanner() {
      try {
        const container = document.getElementById('qr-reader')
        if (container) container.innerHTML = ''

        this.scanner = new Html5Qrcode('qr-reader', {
          verbose: false,
          experimentalFeatures: { useBarCodeDetectorIfSupported: false }
        })

        await this.scanner.start(
          this._selectedCamera || this.cameraId,
          {
            fps: 10,
            qrbox: (viewfinderWidth, viewfinderHeight) => {
              const size = Math.min(
                250,
                Math.floor(Math.min(viewfinderWidth, viewfinderHeight) * 0.8)
              );

              return { width: size, height: size };
            },
            // aspectRatio removido: valores estrictos causan
            // OverconstrainedError en algunas cámaras
            formatsToSupport: [
              Html5QrcodeSupportedFormats.QR_CODE,
              Html5QrcodeSupportedFormats.CODE_128,
              Html5QrcodeSupportedFormats.EAN_13,
              Html5QrcodeSupportedFormats.EAN_8,
              Html5QrcodeSupportedFormats.UPC_A,
              Html5QrcodeSupportedFormats.UPC_E
            ]
          },
          (decodedText) => {
            if (this.result === decodedText) return
            this.result = decodedText
            this.$emit('scan', decodedText)
          },
          () => {}
        )
      } catch (error) {
        this.errorName = error.name || 'UnknownError'
      }
    },
    async stopScanner() {
      try {
        if (this.scanner) {
          if (this.scanner.isScanning) {
            await this.scanner.stop()
          }
          await this.scanner.clear()
          this.scanner = null
        }
      } catch (error) {
        this.scanner = null
      }
    }
  }
}
</script>

<style scoped>
.qr-scanner-wrapper {
  width: 100%;
  height: 100%;
}

.qr-scanner-wrapper >>> #qr-reader {
  width: 100% !important;
  height: 100% !important;
}
</style>

<style>
/* html5-qrcode inyecta estilos inline; los sobreescribimos globalmente */
#qr-reader {
  border: none !important;
}

#qr-reader video {
  /* La caja del video debe conservar la proporción nativa de la cámara:
     html5-qrcode usa clientWidth/clientHeight del video para recortar el
     canvas; si se fuerza a 100% x 100%, la proporción cambia y el frame
     queda distorsionado en el canvas. */
  width: 100% !important;
  height: auto !important;
  display: block;
}

#qr-reader__scan_region {
  width: 100% !important;
  height: 100% !important;
}

#qr-reader__scan_region img {
  display: none;
}

#qr-reader__dashboard {
  display: none;
}
</style>